import {
  FontStyle,
  FontVariant,
  getPrimaryFontFamily,
  getSelfHostedFont,
  getSubsetUnicodeRange,
} from "./font-manifest";

export interface FontUsage {
  family: string;
  weight: number;
  style: FontStyle;
}

interface FontNode {
  customStyle?: Record<string, string> | null;
  value?: string;
  textClasses?: Record<string, string>;
  textMobileClasses?: Record<string, string>;
  nodes?: FontNode[];
}

const normalizeWeight = (weight: string): number => {
  if (weight === "bold") return 700;
  const parsedWeight = Number.parseInt(weight, 10);
  return Number.isNaN(parsedWeight) ? 400 : parsedWeight;
};

const getClosestVariant = (variants: FontVariant[], usage: FontUsage): FontVariant | undefined => {
  const sameStyle = variants.filter((variant) => variant.style === usage.style);
  const candidates = sameStyle.length ? sameStyle : variants.filter((variant) => variant.style === "normal");

  return candidates.sort(
    (left, right) => Math.abs(left.weight - usage.weight) - Math.abs(right.weight - usage.weight),
  )[0];
};

const addFontUsage = (usage: Map<string, FontUsage>, family?: string, weight = 400, style: FontStyle = "normal") => {
  const primaryFamily = getPrimaryFontFamily(family);
  if (!primaryFamily || !getSelfHostedFont(primaryFamily)) return;

  usage.set(`${primaryFamily}:${weight}:${style}`, { family: primaryFamily, weight, style });
};

const getInlineStyleValues = (html: unknown, property: string): string[] => {
  if (typeof html !== "string") return [];

  return Array.from(
    html.matchAll(new RegExp(`${property}\\s*:\\s*(?:["']([^"']+)["']|([^;]+))`, "gi")),
  )
    .map((match) => (match[1] || match[2])?.trim())
    .filter((value): value is string => Boolean(value));
};

export const collectFontUsageFromNodes = (nodes: FontNode[]): FontUsage[] => {
  const usage = new Map<string, FontUsage>();

  const addUsageFromCss = (css: string, inherited: FontUsage | undefined) => {
    const weight = normalizeWeight(getInlineStyleValues(css, "font-weight")[0] || String(inherited?.weight || 400));
    const style = getInlineStyleValues(css, "font-style")[0] === "italic" ? "italic" : inherited?.style || "normal";
    getInlineStyleValues(css, "font-family").forEach((family) => addFontUsage(usage, family, weight, style));
  };

  const visit = (node: FontNode, inherited: FontUsage | undefined) => {
    const nodeStyle = node.customStyle || {};
    const nodeUsage: FontUsage | undefined = nodeStyle.fontFamily
      ? {
          family: getPrimaryFontFamily(nodeStyle.fontFamily) || nodeStyle.fontFamily,
          weight: normalizeWeight(nodeStyle.fontWeight || String(inherited?.weight || 400)),
          style: nodeStyle.fontStyle === "italic" ? "italic" : inherited?.style || "normal",
        }
      : inherited;

    if (nodeUsage) addFontUsage(usage, nodeUsage.family, nodeUsage.weight, nodeUsage.style);

    if (typeof node.value === "string") addUsageFromCss(node.value, nodeUsage);
    Object.values(node.textClasses || {}).forEach((css) => addUsageFromCss(css, nodeUsage));
    Object.values(node.textMobileClasses || {}).forEach((css) => addUsageFromCss(css, nodeUsage));

    node.nodes?.forEach((child) => visit(child, nodeUsage));
  };

  nodes.forEach((node) => visit(node, undefined));
  return Array.from(usage.values());
};

export const collectFontUsage = (root: ParentNode, sourceWindow: Window = window): FontUsage[] => {
  const usage = new Map<string, FontUsage>();
  const elements = root.querySelectorAll<HTMLElement>("*");

  elements.forEach((element) => {
    const style = sourceWindow.getComputedStyle(element);
    const family = getPrimaryFontFamily(style.fontFamily);
    if (!getSelfHostedFont(family) || !family) return;

    addFontUsage(
      usage,
      family,
      normalizeWeight(style.fontWeight),
      (style.fontStyle === "italic" ? "italic" : "normal") as FontStyle,
    );
  });

  return Array.from(usage.values());
};

export const collectFontUsageFromVisibleText = (
  root: ParentNode,
  sourceWindow: Window = window,
): FontUsage[] => {
  const usage = new Map<string, FontUsage>();

  root.querySelectorAll<HTMLElement>("*").forEach((element) => {
    const hasOwnText = Array.from(element.childNodes).some(
      (node) => node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim()),
    );
    if (!hasOwnText) return;

    const style = sourceWindow.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return;

    addFontUsage(
      usage,
      getPrimaryFontFamily(style.fontFamily),
      normalizeWeight(style.fontWeight),
      (style.fontStyle === "italic" ? "italic" : "normal") as FontStyle,
    );
  });

  return Array.from(usage.values());
};

export const createFontFaceCss = (usage: FontUsage[]): string => {
  const faces = usage.flatMap((item) => {
    const font = getSelfHostedFont(item.family);
    const variant = font ? getClosestVariant(font.variants, item) : undefined;
    if (!variant) return [];

    return variant.assets.filter((asset) => asset.url).map(
      (asset) => `@font-face{font-family:"${item.family}";font-style:${variant.style};font-weight:${variant.weight};font-display:swap;src:url("${asset.url}") format("woff2");unicode-range:${getSubsetUnicodeRange(asset.subset)};}`,
    );
  });

  return Array.from(new Set(faces)).join("");
};

export const prependFontFaceStyles = (
  html: string,
  root: ParentNode,
  sourceWindow: Window = window,
): string => {
  const fontFaces = createFontFaceCss(
    collectFontUsageFromVisibleText(root, sourceWindow),
  );

  return fontFaces
    ? `<style>${fontFaces}*{font-synthesis:none;}</style>${html}`
    : html;
};
